import { reportError } from '@/shared';
import { ShelfRequestError, shelfLoad, shelfRemoveItem, shelfStore } from '@/shared/api/lotus';
import { z } from 'zod';

import type { DocumentDraftStore } from '../model/documentDraftStore';
import type { DocumentDraft } from '../model/documentDraftTypes';

const draftModifierSchema = z.strictObject({
  userId: z.string().min(1),
  displayName: z.string().nullable(),
});

const draftContentSchema = z.strictObject({
  bodyMarkdown: z.string(),
  attachments: z.array(
    z.strictObject({
      attachmentId: z.string().min(1),
      filename: z.string(),
      mimeType: z.string(),
    })
  ),
  recipient: z.record(z.string(), z.unknown()).optional(),
});

const draftSchema = z.strictObject({
  id: z.string().min(1),
  // Keep accepting records written before patientId was removed from the
  // draft shape, while preventing other unknown fields from being stored.
  patientId: z.string().optional(),
  content: draftContentSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
  modifiedBy: draftModifierSchema,
});

function reportMalformedRecord(recordType: string, error: z.ZodError): void {
  reportError(error, {
    surface: 'document-draft-shelf',
    feature: 'document-editor',
    operation: 'loadDocumentDrafts',
    tags: { recordType },
  });
}

function readDraft(value: unknown): DocumentDraft | null {
  const parsed = draftSchema.safeParse(value);
  if (!parsed.success) {
    reportMalformedRecord('draft', parsed.error);
    return null;
  }

  return {
    id: parsed.data.id,
    content: parsed.data.content,
    createdAt: parsed.data.createdAt,
    updatedAt: parsed.data.updatedAt,
    modifiedBy: parsed.data.modifiedBy,
  };
}

export async function loadDocumentDrafts(
  patientId: string,
  store: DocumentDraftStore
): Promise<DocumentDraft[]> {
  try {
    const result = await shelfLoad(patientId, store.shelfKey);

    return result.value
      .map((value) => readDraft(value))
      .filter((draft): draft is DocumentDraft => draft !== null);
  } catch (error) {
    if (error instanceof ShelfRequestError && error.status === 404) {
      return [];
    }
    throw error;
  }
}

export async function saveDocumentDrafts(
  patientId: string,
  drafts: DocumentDraft[],
  store: DocumentDraftStore
): Promise<void> {
  await shelfStore(patientId, store.shelfKey, drafts as unknown as Array<Record<string, unknown>>, {
    kind: store.kind,
    schemaVersion: store.schemaVersion,
  });
}

export async function removeDocumentDraft(
  patientId: string,
  draftId: string,
  store: DocumentDraftStore
): Promise<void> {
  await shelfRemoveItem(patientId, store.shelfKey, draftId);
}
